<script setup lang="ts">
/**
 * GridServiceLinks
 *
 * Navigation cards linking to related services.
 * Each card has: icon, title, description, and arrow link.
 * Used for cross-selling and internal navigation.
 */

import type { GridServiceLinksProps } from "~~/shared/types";

const props = withDefaults(defineProps<GridServiceLinksProps>(), {
  columns: 3,
  sectionBg: "neutral",
});

const { resolveUrl } = useServiceRoutes();

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const gridColsClass = computed(() => {
  switch (props.columns) {
    case 2:
      return "sm:grid-cols-2";
    case 4:
      return "sm:grid-cols-2 lg:grid-cols-4";
    case 3:
    default:
      return "sm:grid-cols-2 lg:grid-cols-3";
  }
});
</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Header -->
      <div class="mb-12 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ title }}
        </h2>
      </div>

      <!-- Service Cards Grid -->
      <div :class="['grid gap-8', gridColsClass]">
        <NuxtLink
          v-for="(item, index) in items"
          :key="index"
          :to="resolveUrl(item.targetKey)"
          class="group relative overflow-hidden rounded-2xl border border-neutral-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
        >
          <!-- Icon -->
          <div class="mb-4">
            <div
              class="flex size-12 items-center justify-center rounded-xl bg-mw-accent-50 transition-colors duration-300 group-hover:bg-mw-accent-100"
            >
              <Icon :name="item.icon" size="40" class="text-mw-accent-500" />
            </div>
          </div>

          <!-- Title -->
          <h3 class="mb-2 text-lg font-bold text-slate-900">
            {{ item.title }}
          </h3>

          <!-- Description -->
          <p class="mb-4 text-sm leading-relaxed text-neutral-600">
            {{ item.description }}
          </p>

          <!-- Arrow Link -->
          <div class="flex items-center gap-1 text-sm font-semibold text-primary-400">
            <span>Learn more</span>
            <Icon
              name="lucide:arrow-right"
              size="16"
              class="transition-transform duration-300 group-hover:translate-x-1"
            />
          </div>

          <!-- Hover Accent Line -->
          <div
            class="absolute bottom-0 left-0 right-0 h-1 origin-left scale-x-0 bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
          ></div>
        </NuxtLink>
      </div>
    </div>
  </section>
</template>
